/**
 * @file 用户资产网关模块，主要好几个地方要用，并且需要在登录之后连接
 * @date 2021-07-12
 */

import { ram } from '@sfe/bone-cache';
import { BoneWebsocket } from '@sfe/bone-websocket';
import { WS_DOMAIN_LIST, getOrigin, getUsableOrigin } from '@/config/env';
import { i18n } from '@/locales/i18n';
import { monitor } from '@/utils/monitor';
import OptionWS from '@/utils/optionWS';
import { parseUrl } from '@/utils/url';
import { getWsToken } from '@/utils/wsToken';

const CACHE_KEY = 'user-gateway';
const CACHE_KEY_UTA = 'user-gateway-uta';

/**
 * 获取当前用户网关实例
 *
 * @return {Object}
 */
export function get() {
    return ram.get(CACHE_KEY);
}

/**
 * 关闭当前用户网关
 *
 * @return {Promise}
 */
export function logout() {
    if (ram.get(CACHE_KEY)) {
        ram.get(CACHE_KEY).close();
        ram.remove(CACHE_KEY);
    }
    return Promise.resolve();
}

/**
 * 打开并登录当前的用户网关
 *
 * @param {boolean} isUTA 是否统一账户
 * @return {Promise}
 */
export async function login({ isUTA, isPrivate = false }) {
    const token = await getWsToken();
    return new Promise((done) => {
        const catchKey = isUTA ? CACHE_KEY_UTA : CACHE_KEY;
        if (ram.get(catchKey)) {
            ram.get(catchKey).login(() => done(ram.get(catchKey)));
        } else if (token) {
            // @store引用了modules/balance
            // modules/balance引用了此方法（loginUserWebSocket）
            // 此文件引用@store会引起循环调用问题
            const origin = getOrigin({ type: 'ws' });
            const locale = String(i18n.global.locale.value).toLowerCase();
            const ws = isUTA
                ? new OptionWS({
                    url: `${origin}/trade/option/usdc/private/v1?lang=${locale}`,
                    pingInterval: 20 * 1000,
                    autoConnect: !window.sfe.prerender,
                    getToken: () => getWsToken(true),
                })
                : new BoneWebsocket({
                    url: isPrivate ? `${origin}/private` : `${origin}/spot/api/ws/user?lang=${locale}`,
                    isPrivate,
                    pingInterval: 20 * 1000,
                    autoConnect: !window.sfe.prerender,
                    getToken: () => getWsToken(true),
                });

            ws.on('close', (data) => {
                const { origin: currentOrigin, pathname, search } = parseUrl(data.url);
                // 初始ws连接失败后，切换新的链路进行连接
                const { origin: newOrigin, isLoop } = getUsableOrigin('userGateWay', {
                    originList: WS_DOMAIN_LIST,
                    currentOrigin,
                });
                const url = `${newOrigin}${pathname}${search.split('&')?.[0]}`;
                if (isLoop) {
                    ws.changeLine({ url });
                }

                if (!navigator.onLine) return; // 没有网络不上报
                monitor.counter('ws_close', { r: pathname, m: search, ei: data.errorInfo });
            });

            ws.on('open', (data) => {
                const url = parseUrl(data.url);
                monitor.gauge('ws_open', data.duration, { r: url.pathname, m: url.search });
            });

            ram.set(catchKey, ws);
            ws.login(token, () => {
                done(ram.get(catchKey));
            });
        }
    });
}
